import React, { ReactElement, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';

import { createUrl, request } from 'app/utils';
import { Box, Flex, Loading, Page, Section, Typography } from 'components';
import { LastBadges } from 'features/badges';
import { useSchool } from 'features/school';
import { useBadges, StudentBadgesApiResponse } from 'features/badges';
import { useProducts, ProductsApiResponse, ProductsCompletedApiResponse, ProductsList } from 'features/products';
import styled from 'styled-components';
import { useMenu } from 'features/menu';

import usestorage from 'app/utils/storage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HeaderPerfil } from 'features/account/components';
import SubmenuVertical from 'components/submenuVertical';

const MyProductsView = (): ReactElement => {
  const { apiGatewayUrl, schoolUuid } = useSchool();
  const [loading, setLoading] = useState(true);
  const { setBackPathname, clearBackPathname } = useMenu();
  const [subAreaIndex, setSubAreaIndex] = useState(0);

  const { setStudentBadges, clearStudentBadges } = useBadges();

  const { productsMy, setProductsMy, clearProductsMy, productsCompleted, setProductsCompleted, clearProductsCompleted } = useProducts();

  const storage = usestorage('localStorage');
  const currentDashboard = storage.get('currentDashboard');

  const [searchParams] = useSearchParams();
  const currentDashboardArea = searchParams.get('dashboardAreaUuid');

  const navigate = useNavigate();

  const fetch = async () => {
    await Promise.all([
      request({
        url: `/api/v1/School/StudentBadges`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          productSlug: null,
          specialOnly: null,
          itensPerQuery: 8,
        },
      }).then((response: AxiosResponse<StudentBadgesApiResponse>) => {
        setStudentBadges(response.data);
      }),
      request({
        url: '/api/v1/School/MyNotCompletedCourses',
        method: 'get',
        baseURL: apiGatewayUrl,
        params: {
          ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
          ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
        },
      }).then((response: AxiosResponse<ProductsApiResponse>) => {
        setProductsMy(response.data);
      }),
      request({
        url: '/api/v1/School/MyCompletedCourses',
        method: 'get',
        baseURL: apiGatewayUrl,
        params: {
          ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
          ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
        },
      }).then((response: AxiosResponse<ProductsCompletedApiResponse>) => {
        setProductsCompleted(response.data);
      }),
    ]).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetch();

    setBackPathname(createUrl('dashboard'));

    return () => {
      clearStudentBadges();
      clearProductsMy();
      clearProductsCompleted();
      clearBackPathname();
    };
  }, []);

  const isBenndorf = schoolUuid === 'cfaf550b-037d-45b2-9f84-979fb01185d4';
  const isMeuPlanoCrypto = schoolUuid === '20b929ad-c773-46e2-93de-991988587835';

  let headTitle = 'Meus cursos';
  let currentTitle = 'Meus cursos atuais';
  let completedTitle = 'Meus cursos concluídos';

  if (isBenndorf) {
    headTitle = 'Meus produtos';
    currentTitle = 'Meus produtos atuais';
    completedTitle = 'Meus produtos concluídos';
  } else if (isMeuPlanoCrypto) {
    headTitle = 'Conteúdos';
    currentTitle = 'Conteúdos disponíveis';
    completedTitle = 'Conteúdos concluídos';
  }

  if (loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title={headTitle}>
      <HeaderPerfil areaIndex={1} onAreaClick={(value) => navigate(createUrl(value))} />

      <Flex flexDirection="row" bg="white">
        <SubmenuVertical
          areas={['Cursos em andamento', 'Cursos concluídos']}
          areaIndex={subAreaIndex}
          onAreaClick={(index) => setSubAreaIndex(index)}
        />
        <Flex py={{ m: 30, t: 60, d: 60 }} flexGrow={1} flexDirection="column">
          {subAreaIndex === 0 ? (
            <Flex bg="white" flexGrow={0}>
              <Section flexDirection="column">
                <ProductsList products={productsMy} active />
              </Section>
            </Flex>
          ) : null}
          {subAreaIndex === 1 ? (
            Boolean(productsCompleted && productsCompleted.length) ? (
              <Flex bg="white" flexGrow={0}>
                <Section flexDirection="column">
                  <ProductsList products={productsCompleted} completed />
                </Section>
              </Flex>
            ) : (
              <Flex ml="2%">
                <Typography fontWeight="bold">Nenhum curso concluído.</Typography>
              </Flex>
            )
          ) : null}
        </Flex>
      </Flex>
    </Page>
  );
};

export default MyProductsView;
